import axios from "axios"
import { supabase } from "../supabaseClient" // Import the supabase client

const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL ? `${process.env.REACT_APP_SERVER_URL}/api/voice` : "/api/voice",
  headers: {
    "Content-Type": "application/json",
  },
})

// Add a request interceptor to include the user ID in the headers
api.interceptors.request.use(
  async (config) => {
    // Get the current session
    const {
      data: { session },
    } = await supabase.auth.getSession()

    // If there's a session, add the user ID to the headers
    if (session) {
      config.headers["user-id"] = session.user.id
    }

    // Check if admin has selected a company
    try {
      const selectedCompanyName = localStorage.getItem("selectedCompanyName")
      if (selectedCompanyName) {
        config.headers["x-company-name"] = selectedCompanyName
      }
    } catch (e) {
      // Silently fail if localStorage access fails
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export interface KnowledgeBaseSource {
  type: "document" | "text" | "url"
  source_id: string
  filename?: string
  file_url?: string
  title?: string
  content_url?: string
  url?: string
}

export interface KnowledgeBase {
  knowledge_base_id: string
  knowledge_base_name: string
  status: "in_progress" | "complete" | "error"
  knowledge_base_sources: KnowledgeBaseSource[]
  enable_auto_refresh?: boolean
  last_refreshed_timestamp?: number
}

export interface KnowledgeBaseText {
  title: string
  text: string
}

export const knowledgeBaseService = {
  list: async (companyName?: string): Promise<KnowledgeBase[]> => {
    const url = companyName ? `/knowledge-bases?company_name=${encodeURIComponent(companyName)}` : "/knowledge-bases"
    const response = await api.get<KnowledgeBase[]>(url)
    return response.data
  },

  get: async (knowledgeBaseId: string): Promise<KnowledgeBase> => {
    const response = await api.get<KnowledgeBase>(`/knowledge-bases/${knowledgeBaseId}`)
    return response.data
  },

  create: async (
    name: string,
    texts?: KnowledgeBaseText[],
    files?: File[],
    urls?: string[],
    enableAutoRefresh?: boolean,
    companyName?: string, // Add companyName parameter for admin override
  ): Promise<KnowledgeBase> => {
    const formData = new FormData()
    formData.append("name", name)

    // Get the current session
    const {
      data: { session },
    } = await supabase.auth.getSession()

    // If there's a session, add the user ID to the form data
    if (session) {
      formData.append("userId", session.user.id)
    }

    if (companyName) {
      formData.append("company_name", companyName)
    }

    if (enableAutoRefresh !== undefined) {
      formData.append("enableAutoRefresh", String(enableAutoRefresh))
    }

    if (texts && texts.length > 0) {
      formData.append("texts", JSON.stringify(texts))
    }

    if (urls && urls.length > 0) {
      formData.append("urls", JSON.stringify(urls))
    }

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file)
      })
    }

    const response = await api.post<KnowledgeBase>("/knowledge-bases", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response.data
  },

  delete: async (knowledgeBaseId: string): Promise<void> => {
    await api.delete(`/knowledge-bases/${knowledgeBaseId}`)
  },

  addSources: async (
    knowledgeBaseId: string,
    texts?: KnowledgeBaseText[],
    files?: File[],
    urls?: string[],
  ): Promise<KnowledgeBase> => {
    const formData = new FormData()

    if (texts && texts.length > 0) {
      formData.append("texts", JSON.stringify(texts))
    }

    if (urls && urls.length > 0) {
      formData.append("urls", JSON.stringify(urls))
    }

    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append("files", file)
      })
    }

    const response = await api.post<KnowledgeBase>(`/knowledge-bases/${knowledgeBaseId}/sources`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })

    return response.data
  },

  removeSource: async (knowledgeBaseId: string, sourceId: string): Promise<void> => {
    await api.delete(`/knowledge-bases/${knowledgeBaseId}/sources/${sourceId}`)
  },
}
